import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Modal } from "reactstrap";
import swal from "sweetalert";
import {
  ChevronRight,
  FolderTree,
  Layers,
  MoreHorizontal,
  Pencil,
  Plus,
  Search as SearchIcon,
  Tags,
  Trash2,
  X,
} from "lucide-react";

import { getCategory } from "../../../redux/category/action";
import { getAllTypes } from "../../../redux/type/action";
import axiosInstance from "../../../redux/api";
import { usePermissions } from "../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../lib/permissions";
import ModalDelete from "../../Common/Popup/ModalDelete";
import EditorDrawer from "./EditorDrawer";
import LinkPicker from "./LinkPicker";
import "./CatalogWorkbench.css";

const TABS = [
  {
    key: "categories",
    name: "Categories",
    icon: Layers,
    entity: "category",
    newLabel: "New category",
    desc: "The main sections of the store — what shoppers see first.",
  },
  {
    key: "subcats",
    name: "Subcategories",
    icon: FolderTree,
    entity: "subcategory",
    newLabel: "New subcategory",
    desc: "Groups inside each category, like Men or Women inside Fragrances.",
  },
  {
    key: "types",
    name: "Product types",
    icon: Tags,
    entity: "type",
    newLabel: "New product type",
    desc: "What kind of product something is — Parfum, Body Mist, Lipstick…",
  },
];

const CatalogWorkbench = () => {
  const dispatch = useDispatch();
  const { hasAction } = usePermissions();

  const categoriesRaw = useSelector((s) => s.category?.categorylist);
  const allTypes = useSelector((s) => s.reusableSlice?.typeList);
  const categories = useMemo(() => categoriesRaw || [], [categoriesRaw]);
  const typeList = useMemo(
    () => (Array.isArray(allTypes) ? allTypes : []),
    [allTypes],
  );

  const canCreate = hasAction(MENU.CATALOGUE, PERMISSION.CREATE);
  const canDelete = hasAction(MENU.CATALOGUE, PERMISSION.DELETE);

  const [tab, setTab] = useState("categories");
  const [searchtext, setSearchtext] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [openMenu, setOpenMenu] = useState(null); // "cat:id" | "sub:id" | "type:id"

  // Editor drawer: null, or { entity, mode, id? }. A bump of refreshTick
  // re-runs all loads after a successful save or a delete.
  const [editor, setEditor] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const refresh = useCallback(() => setRefreshTick((n) => n + 1), []);

  // One-time loads: categories + the full type dictionary.
  useEffect(() => {
    dispatch(getCategory({ page: 1, per_page: 500, search_key: "", status: "" }));
    dispatch(getAllTypes({ page: 1, per_page: 500, search_key: "", status: "" }));
  }, [dispatch, refreshTick]);

  // All subcategories in one request — each record carries its parent
  // `categories`, which is what the Categories tab chips and the orphan
  // tag are derived from.
  const [allSubcats, setAllSubcats] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoadingSubs(true);
    axiosInstance
      .get("admin/sub-categories", {
        params: { page: 1, per_page: 500, search_key: "", status: "" },
      })
      .then((r) => r.data?.response || [])
      .catch(() => [])
      .then((list) => {
        if (cancelled) return;
        setAllSubcats(list);
        setLoadingSubs(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  // Quick-create handshake: other screens link here with
  // `?create=category|subcategory|type` to open the editor straight into a
  // fresh form. Clear the param once consumed so a refresh doesn't re-open.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const requested = searchParams.get("create");
    if (!requested) return;
    const target = TABS.find((t) => t.entity === requested);
    if (target) {
      setTab(target.key);
      setEditor({ entity: requested, mode: "create" });
    }
    const next = new URLSearchParams(searchParams);
    next.delete("create");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // ---- Lookups ----
  const catById = useCallback(
    (id) => categories.find((c) => Number(c.id) === Number(id)),
    [categories],
  );
  const subsInCat = useCallback(
    (cid) =>
      allSubcats.filter((s) =>
        (s.categories || []).some((c) => Number(c.id) === Number(cid)),
      ),
    [allSubcats],
  );
  // ---- Toasts (with undo — replaces the old window.confirm on unlink) ----
  const [toasts, setToasts] = useState([]);
  const toastSeq = useRef(0);
  const dismissToast = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);
  const pushToast = useCallback(
    (msg, undo) => {
      const id = ++toastSeq.current;
      setToasts((t) => [...t.slice(-2), { id, msg, undo }]);
      setTimeout(() => dismissToast(id), 6000);
    },
    [dismissToast],
  );

  // ---- Subcategory ⇄ Category linking — edits the subcategory's parent set.
  // We re-read the full record first so the PUT doesn't drop other fields,
  // then patch the local list (no refetch needed). ----
  const putSubcategoryParents = async (scId, nextCatIds) => {
    try {
      const rec = (await axiosInstance.get(`admin/sub-categories/${scId}`)).data
        ?.response;
      if (!rec) throw new Error("not found");
      await axiosInstance.put("admin/sub-categories", {
        id: rec.id,
        title: rec.title,
        ar_title: rec.ar_title,
        image: rec.image,
        category_ids: nextCatIds,
        meta_title: rec.meta_title || "",
        page_title: rec.page_title || "",
        meta_h1_tag: rec.meta_h1_tag || "",
        meta_h2_tag: rec.meta_h2_tag || "",
        meta_description: rec.meta_description || "",
        status: rec.status,
        position: rec.position,
        home_view: rec.home_view,
        gender: rec.gender || 1,
      });
      setAllSubcats((list) =>
        list.map((s) =>
          s.id === scId
            ? { ...s, categories: nextCatIds.map((id) => catById(id) || { id }) }
            : s,
        ),
      );
      return true;
    } catch (e) {
      swal({
        text:
          e?.response?.data?.message || "Could not update the subcategory link.",
        icon: "error",
        button: "Close",
      });
      return false;
    }
  };
  const parentIdsOf = (sub) =>
    (sub.categories || []).map((c) => Number(c.id)).filter(Boolean);
  const addSubToCats = async (sub, catIds) => {
    const prev = parentIdsOf(sub);
    const next = [...new Set([...prev, ...catIds.map(Number)])];
    if (await putSubcategoryParents(sub.id, next)) {
      pushToast(
        `Added ${sub.title} to ${catIds.length} ${catIds.length === 1 ? "category" : "categories"}.`,
        () => putSubcategoryParents(sub.id, prev),
      );
    }
  };
  const addSubsToCat = async (cat, subIds) => {
    const done = [];
    for (const sid of subIds) {
      const sub = allSubcats.find((s) => s.id === sid);
      if (!sub) continue;
      const prev = parentIdsOf(sub);
      const next = [...new Set([...prev, Number(cat.id)])];
      // eslint-disable-next-line no-await-in-loop
      if (await putSubcategoryParents(sid, next)) done.push({ sid, prev });
    }
    if (done.length) {
      pushToast(
        `Added ${done.length} ${done.length === 1 ? "subcategory" : "subcategories"} to ${cat.title}.`,
        async () => {
          for (const { sid, prev } of done) {
            // eslint-disable-next-line no-await-in-loop
            await putSubcategoryParents(sid, prev);
          }
        },
      );
    }
  };
  const removeSubFromCat = async (sub, cat) => {
    const prev = parentIdsOf(sub);
    const next = prev.filter((x) => x !== Number(cat.id));
    if (await putSubcategoryParents(sub.id, next)) {
      pushToast(`Removed ${sub.title} from ${cat.title}.`, () =>
        putSubcategoryParents(sub.id, prev),
      );
    }
  };

  // ---- Delete (destroys the entity everywhere) ----
  const [deleteTarget, setDeleteTarget] = useState(null);
  const doDelete = async () => {
    if (!deleteTarget) return;
    const { kind, item } = deleteTarget;
    const url =
      kind === "category"
        ? `admin/categories/${item.id}`
        : kind === "subcategory"
          ? `admin/sub-categories/${item.id}`
          : `admin/types/${item.id}`;
    try {
      await axiosInstance.delete(url);
      swal({ text: "Deleted.", icon: "success", button: "Close", timer: 1100 });
      refresh();
    } catch (e) {
      swal({
        text:
          e?.response?.data?.message ||
          "Could not delete. It may still be in use.",
        icon: "error",
        button: "Close",
      });
    }
  };

  // ---- Row overflow menu: close on outside click / Esc ----
  useEffect(() => {
    if (!openMenu) return undefined;
    const onDown = (e) => {
      if (!e.target.closest(".cw-menu") && !e.target.closest("[data-menubtn]")) {
        setOpenMenu(null);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openMenu]);

  // ---- Editor plumbing ----
  const editEntity = (entity, id) => setEditor({ entity, mode: "edit", id });
  const handleSaved = useCallback(() => {
    setEditor(null);
    setRefreshTick((n) => n + 1);
  }, []);
  const handleCancel = useCallback(() => setEditor(null), []);

  // ---- Multi-select picker: null | { kind, target } ----
  const [picker, setPicker] = useState(null);
  const pickerProps = useMemo(() => {
    if (!picker) return null;
    if (picker.kind === "subs-for-cat") {
      const cat = picker.target;
      const linked = new Set(subsInCat(cat.id).map((s) => s.id));
      return {
        title: `Add subcategories to ${cat.title}`,
        note: "Pick as many as you need. Don't see one? Create it from the Subcategories tab first.",
        verb: "Add",
        candidates: allSubcats
          .filter((s) => !linked.has(s.id))
          .map((s) => ({ id: s.id, label: s.title || `#${s.id}` })),
        onAdd: (ids) => addSubsToCat(cat, ids),
      };
    }
    // cats-for-sub
    const sub = picker.target;
    const linked = new Set(parentIdsOf(sub).map(String));
    return {
      title: `Add ${sub.title} to categories`,
      note: "A subcategory can live in more than one category.",
      verb: "Add",
      candidates: categories
        .filter((c) => !linked.has(String(c.id)))
        .map((c) => ({ id: c.id, label: c.title || `#${c.id}` })),
      onAdd: (ids) => addSubToCats(sub, ids),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picker, allSubcats, categories, subsInCat]);

  // ---- Cross-tab chip navigation ----
  const goTo = (tabKey, search) => {
    setTab(tabKey);
    setSearchtext(search);
    setStatusFilter("");
    setOpenMenu(null);
  };
  const switchTab = (key) => {
    setTab(key);
    setSearchtext("");
    setStatusFilter("");
    setOpenMenu(null);
  };

  // ---- Filtering ----
  const currentItems = useMemo(() => {
    const src =
      tab === "categories" ? categories : tab === "subcats" ? allSubcats : typeList;
    const q = searchtext.trim().toLowerCase();
    return src.filter((x) => {
      const name = (tab === "types" ? x.name : x.title) || "";
      const arName = x.ar_title || x.ar_name || "";
      const matchStatus = statusFilter
        ? x.status === parseInt(statusFilter, 10)
        : true;
      const matchSearch = q
        ? name.toLowerCase().includes(q) || arName.includes(searchtext.trim())
        : true;
      return matchStatus && matchSearch;
    });
  }, [tab, categories, allSubcats, typeList, searchtext, statusFilter]);

  const activeTab = TABS.find((t) => t.key === tab);

  // ---- Shared row fragments ----
  const countBadge = (item) =>
    Number.isFinite(Number(item.product_count)) ? (
      <span
        className="cw-count"
        title={`${item.product_count} ${Number(item.product_count) === 1 ? "product" : "products"} linked`}
      >
        ({Number(item.product_count).toLocaleString()})
      </span>
    ) : null;

  const statusBadge = (item) => (
    <span
      className={`nl-badge cw-row__status ${item.status === 1 ? "nl-badge--on" : "nl-badge--off"}`}
    >
      <span className="nl-badge__dot" aria-hidden="true" />
      {item.status === 1 ? "Active" : "Inactive"}
    </span>
  );

  const rowActions = (menuKey, kind, item) => (
    <div className="cw-rowactions">
      <button
        type="button"
        className="cw-editbtn"
        onClick={() =>
          editEntity(
            kind === "cat" ? "category" : kind === "sub" ? "subcategory" : "type",
            item.id,
          )
        }
      >
        <Pencil size={12} aria-hidden="true" />
        Edit
      </button>
      {canDelete && (
        <button
          type="button"
          className="cw-morebtn"
          data-menubtn="true"
          aria-label="More actions"
          aria-expanded={openMenu === menuKey}
          onClick={() => setOpenMenu(openMenu === menuKey ? null : menuKey)}
        >
          <MoreHorizontal size={15} aria-hidden="true" />
        </button>
      )}
      {openMenu === menuKey && (
        <div className="cw-menu" role="menu">
          <button
            type="button"
            className="cw-menu__item cw-menu__item--danger"
            role="menuitem"
            onClick={() => {
              setOpenMenu(null);
              setDeleteTarget({
                kind:
                  kind === "cat"
                    ? "category"
                    : kind === "sub"
                      ? "subcategory"
                      : "type",
                item: kind === "type" ? { ...item, title: item.name } : item,
              });
            }}
          >
            <Trash2 size={13} aria-hidden="true" />
            <span>
              Delete everywhere…
              <small>
                Removes this{" "}
                {kind === "cat"
                  ? "category"
                  : kind === "sub"
                    ? "subcategory"
                    : "product type"}{" "}
                from the whole catalog.
              </small>
            </span>
          </button>
        </div>
      )}
    </div>
  );

  const chip = (label, nav, onRemove, removeLabel) => (
    <span className="cw-chip">
      <button
        type="button"
        className="cw-chip__label"
        onClick={nav}
        title="Open in its own tab"
      >
        {label}
      </button>
      {onRemove && (
        <button
          type="button"
          className="cw-chip__x"
          onClick={onRemove}
          title={removeLabel}
          aria-label={removeLabel}
        >
          <X size={11} aria-hidden="true" />
        </button>
      )}
    </span>
  );

  const addChip = (label, onClick) =>
    canCreate ? (
      <button type="button" className="cw-chip--add" onClick={onClick}>
        <Plus size={12} aria-hidden="true" />
        {label}
      </button>
    ) : null;

  // ---- Tab bodies ----
  const renderCategoryRow = (c) => {
    const subs = subsInCat(c.id);
    return (
      <div className="cw-row" key={c.id}>
        <div className="cw-row__name">
          {c.title}
          {countBadge(c)}
        </div>
        {statusBadge(c)}
        {rowActions(`cat:${c.id}`, "cat", c)}
        <div className="cw-rel">
          <span className="cw-rel__label">Subcategories:</span>
          {loadingSubs ? (
            <span className="cw-rel__empty">Loading…</span>
          ) : (
            <>
              {subs.map((s) =>
                chip(
                  s.title,
                  () => goTo("subcats", s.title || ""),
                  canCreate ? () => removeSubFromCat(s, c) : null,
                  `Remove ${s.title} from ${c.title}`,
                ),
              )}
              {subs.length === 0 && (
                <span className="cw-rel__empty">
                  none yet — add the groups shoppers browse, like Men or Women
                </span>
              )}
              {addChip("Add subcategory", () =>
                setPicker({ kind: "subs-for-cat", target: c }),
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderSubcatRow = (s) => {
    const parents = (s.categories || [])
      .map((c) => catById(c.id) || c)
      .filter(Boolean);
    const orphan = parents.length === 0;
    return (
      <div className="cw-row" key={s.id}>
        <div className="cw-row__name">
          {s.title}
          {countBadge(s)}
          {orphan && <span className="cw-warn">not in any category</span>}
        </div>
        {statusBadge(s)}
        {rowActions(`sub:${s.id}`, "sub", s)}
        <div className="cw-rel">
          <span className="cw-rel__label">In categories:</span>
          {parents.map((c) =>
            chip(
              c.title || `#${c.id}`,
              () => goTo("categories", c.title || ""),
              canCreate ? () => removeSubFromCat(s, c) : null,
              `Remove ${s.title} from ${c.title}`,
            ),
          )}
          {addChip("Add to category", () =>
            setPicker({ kind: "cats-for-sub", target: s }),
          )}
        </div>
      </div>
    );
  };

  const renderTypeRow = (t) => (
    <div className="cw-row" key={t.id}>
      <div className="cw-row__name">
        {t.name}
        {countBadge(t)}
      </div>
      {statusBadge(t)}
      {rowActions(`type:${t.id}`, "type", t)}
    </div>
  );

  return (
    <div className="cw-page nl-page">
      {/* Crumb */}
      <div className="nl-topbar">
        <span className="nl-topbar__crumb">Catalog</span>
        <ChevronRight size={12} className="nl-topbar__sep" aria-hidden="true" />
        <span className="nl-topbar__current">Structure</span>
        <span className="nl-topbar__count">
          {categories.length.toLocaleString()}
        </span>
      </div>

      <div className="cw-wrap">
        <header className="cw-head">
          <h1 className="cw-head__title">Catalog</h1>
          <p className="cw-head__sub">
            Categories contain subcategories · edit a subcategory or a product
            type to set which types are allowed inside it.
          </p>
        </header>

        {/* Big tabs */}
        <div className="cw-tabs" role="tablist" aria-label="Catalog sections">
          {TABS.map((t) => {
            const Icon = t.icon;
            const count =
              t.key === "categories"
                ? categories.length
                : t.key === "subcats"
                  ? allSubcats.length
                  : typeList.length;
            return (
              <button
                type="button"
                key={t.key}
                role="tab"
                aria-selected={tab === t.key}
                className={`cw-tab ${tab === t.key ? "is-active" : ""}`}
                onClick={() => switchTab(t.key)}
              >
                <span className="cw-tab__icon">
                  <Icon size={16} aria-hidden="true" />
                </span>
                <span>
                  <span className="cw-tab__name">
                    {t.name}
                    <span className="cw-tab__count">{count}</span>
                  </span>
                  <span className="cw-tab__desc">{t.desc}</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="cw-toolbar">
          <div className="nl-search cw-toolbar__search">
            <SearchIcon size={14} className="nl-search__icon" aria-hidden="true" />
            <input
              type="text"
              placeholder="Search…"
              value={searchtext}
              onChange={(e) => setSearchtext(e.target.value)}
            />
            {searchtext && (
              <button
                type="button"
                className="nl-search__clear"
                onClick={() => setSearchtext("")}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <div className="nl-chips">
            <button
              type="button"
              className={`nl-chipfilter ${statusFilter === "" ? "is-active" : ""}`}
              onClick={() => setStatusFilter("")}
            >
              <span className="nl-chipfilter__dot nl-chipfilter__dot--neutral" />
              All
            </button>
            <button
              type="button"
              className={`nl-chipfilter ${statusFilter === "1" ? "is-active" : ""}`}
              onClick={() => setStatusFilter("1")}
            >
              <span className="nl-chipfilter__dot nl-chipfilter__dot--on" />
              Active
            </button>
            <button
              type="button"
              className={`nl-chipfilter ${statusFilter === "2" ? "is-active" : ""}`}
              onClick={() => setStatusFilter("2")}
            >
              <span className="nl-chipfilter__dot nl-chipfilter__dot--off" />
              Inactive
            </button>
          </div>
          <div className="cw-toolbar__spacer" />
          {canCreate && (
            <button
              type="button"
              className="nb-btn nb-btn--primary"
              onClick={() => setEditor({ entity: activeTab.entity, mode: "create" })}
            >
              <Plus size={14} aria-hidden="true" />
              {activeTab.newLabel}
            </button>
          )}
        </div>

        {/* List */}
        <div className="cw-list">
          {currentItems.length === 0 ? (
            <div className="cw-listempty">
              <strong>
                {searchtext.trim()
                  ? `Nothing matches “${searchtext.trim()}”`
                  : "Nothing here yet"}
              </strong>
              {searchtext.trim()
                ? "Try a different word, or check the other two tabs."
                : canCreate
                  ? "Use the button above to create the first one."
                  : ""}
            </div>
          ) : tab === "categories" ? (
            currentItems.map(renderCategoryRow)
          ) : tab === "subcats" ? (
            currentItems.map(renderSubcatRow)
          ) : (
            currentItems.map(renderTypeRow)
          )}
        </div>
      </div>

      <EditorDrawer editor={editor} onSaved={handleSaved} onCancel={handleCancel} />

      <LinkPicker
        open={!!picker}
        title={pickerProps?.title || ""}
        note={pickerProps?.note || ""}
        verb={pickerProps?.verb || "Add"}
        candidates={pickerProps?.candidates || []}
        onAdd={(ids) => {
          const add = pickerProps?.onAdd;
          setPicker(null);
          if (add) add(ids);
        }}
        onClose={() => setPicker(null)}
      />

      <Modal isOpen={!!deleteTarget} toggle={() => setDeleteTarget(null)}>
        {deleteTarget && (
          <ModalDelete
            itemType={
              deleteTarget.kind === "category"
                ? "Category"
                : deleteTarget.kind === "subcategory"
                  ? "Subcategory"
                  : "Type"
            }
            modalTitle={
              deleteTarget.kind === "category"
                ? "Category"
                : deleteTarget.kind === "subcategory"
                  ? "Subcategory"
                  : "Type"
            }
            setModal={() => setDeleteTarget(null)}
            currentComponent={deleteTarget.item}
            deleteComponent={doDelete}
          />
        )}
      </Modal>

      {/* Undo toasts */}
      {toasts.length > 0 && (
        <div className="cw-toasts" role="status" aria-live="polite">
          {toasts.map((t) => (
            <div className="cw-toast" key={t.id}>
              <span>{t.msg}</span>
              {t.undo && (
                <button
                  type="button"
                  className="cw-toast__undo"
                  onClick={() => {
                    dismissToast(t.id);
                    t.undo();
                  }}
                >
                  Undo
                </button>
              )}
              <button
                type="button"
                className="cw-toast__x"
                onClick={() => dismissToast(t.id)}
                aria-label="Dismiss"
              >
                <X size={12} aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CatalogWorkbench;
